import { Trans, t } from "@web-monorepo/i18n/macro";
import { DDSBodyText, DDSButton, DDSDisplayText, DDSIconButton } from "@web-monorepo/dds";
import { IconChevronLeft } from "@web-monorepo/dds-icons";
import { useSideEffectOnMount } from "@web-monorepo/hooks";
import { getImageSrc } from "@web-monorepo/static-assets";
import { useParams } from "react-router-dom";
import logEvent from "src/utils/logEvent";
import { navigateToConundrumsFeedback, navigateToLaunchpad } from "src/pods/conundrums/navigation";
import ConundrumCard from "./ConundrumCard";
import { getDescription as getIntroDescription, getTitle as getIntroTitle } from "./firstActivity/definition";
import {
  getDescription as getMojoLisaDescription,
  getQuestionText as getMojoLisaQuestion,
  getTitle as getMojoLisaTitle,
} from "./mojoLisa/definition";
import Tag from "./Tag";
import styles from "./List.module.css";

const civicServantsImage = getImageSrc("toolkit/src/pods/conundrums/assets/civicServantsListImage@2x.jpg");
const dojosaurusList = getImageSrc("toolkit/src/pods/conundrums/assets/dojosaurusList.png");
const introConundrumImage = getImageSrc("toolkit/src/pods/conundrums/assets/intro@2x.jpg");
const mojoLisaImage = getImageSrc("toolkit/src/pods/conundrums/assets/mojoLisaList.png");
const spaceStationListImage = getImageSrc("toolkit/src/pods/conundrums/assets/spaceStationList.png");

export default function List() {
  const { classId, mountPoint } = useParams<{ classId: string; mountPoint: string }>();

  useSideEffectOnMount(() => {
    logEvent({
      eventName: `conundrums.list`,
    });
  });

  if (!classId || !mountPoint) return null;

  const goToFeedback = () => navigateToConundrumsFeedback(mountPoint, classId);

  return (
    <div className={styles.app}>
      <DDSIconButton
        data-name="conundrums_list:back"
        aria-label={t`Go back`}
        tooltip={{ content: t`Go back`, dataName: "conundrums_list:back:tooltip" }}
        variant="transparent"
        className={styles.backButton}
        onClick={() => navigateToLaunchpad(mountPoint, classId)}
      >
        <IconChevronLeft />
      </DDSIconButton>
      <img
        src={"https://static.classdojo.com/img/ideas_platform/activities/conundrums/conundrums@2x.png"}
        alt={t`Conundrum with Ad Astra`}
        className={styles.heroImage}
      />
      <DDSDisplayText as="h1" level="sm" className={styles.title}>
        <Trans>Conundrums</Trans>
      </DDSDisplayText>
      <DDSBodyText as="p" className={styles.description}>
        <Trans>
          Quick, fun, inquiry-based questions to get students thinking critically, discussing positively, and supporting
          their opinions! Created by Josh Dahn, head of school at Ad Astra.
        </Trans>
      </DDSBodyText>
      <div className={styles.tagsWrapper}>
        <Tag variant="secondary" size="small">
          <Trans>Inquiry-based learning</Trans>
        </Tag>
        <Tag variant="secondary" size="small">
          <Trans>Communication</Trans>
        </Tag>
        <Tag variant="secondary" size="small">
          <Trans>Critical Thinking</Trans>
        </Tag>
        <Tag variant="secondary" size="small">
          <Trans>Discussion</Trans>
        </Tag>
      </div>
      <div className={styles.cardsWrapper}>
        <ConundrumCard
          mojo={introConundrumImage}
          href={`/apps/remote/${mountPoint}/${classId}/conundrums/firstActivity/0`}
          title={getIntroTitle()}
          description={getIntroDescription()}
          duration="5-10"
        />
        <ConundrumCard
          mojo={civicServantsImage}
          href={`/apps/remote/${mountPoint}/${classId}/conundrums/civicServants/0`}
          title={t`Civic Servants`}
          description={
            <span>
              <Trans>
                Mojo is the mayor of Monster Island, and must decide: <b>who should make the most money?</b>
              </Trans>
            </span>
          }
          duration="10-20"
        />

        <ConundrumCard
          mojo={mojoLisaImage}
          href={`/apps/remote/${mountPoint}/${classId}/conundrums/mojoLisa/0`}
          title={getMojoLisaTitle()}
          description={
            <span>
              {getMojoLisaDescription()} <b>{getMojoLisaQuestion()}</b>
            </span>
          }
          duration="10-20"
        />
        <ConundrumCard
          mojo={dojosaurusList}
          href={`/apps/remote/${mountPoint}/${classId}/conundrums/dojosaurus/0`}
          title={t`Dojosaurus`}
          description={
            <span>
              <Trans>Mojo and his friends have discovered a massive dinosaur on someone else's land!</Trans>{" "}
              <b>
                <Trans>Who does it belong to?</Trans>
              </b>
            </span>
          }
          duration="10-20"
        />
        <ConundrumCard
          mojo={spaceStationListImage}
          href={`/apps/remote/${mountPoint}/${classId}/conundrums/spaceStation/0`}
          title={t`Space Station`}
          description={
            <span>
              <Trans>
                Mojo and friends are creating a space station, but they can't decide: <b>Who should be in charge?</b>
              </Trans>
            </span>
          }
          duration="10-20"
        />
      </div>
      <div className={styles.feedback}>
        <DDSBodyText as="p" className={styles.feedbackTitle}>
          <Trans>More Conundrums are coming soon!</Trans>
        </DDSBodyText>
        <DDSBodyText as="p">
          <Trans>Have an idea?</Trans>
        </DDSBodyText>
        <DDSButton
          data-name="list:render:list.let_us_know"
          variant="tertiary"
          onClick={goToFeedback}
          className={styles.letUsKnowButton}
        >
          <Trans>Let us know!</Trans>
        </DDSButton>
      </div>
    </div>
  );
}
